/**
 * The theme, for the control that changes it.
 *
 * `public/theme.js` has already applied one before this module is parsed — that
 * is the half that has to run before paint, and it is deliberately not React's
 * job. This half is only the part a button needs: what is on the document now,
 * and how to change it.
 *
 * An external store rather than `useState`, for the reason the console's own
 * hook gives: the truth is an attribute on `<html>`, not a React value, and two
 * components each holding their own copy of it is how one control ends up
 * showing the opposite of what is on screen.
 *
 * `useSyncExternalStore` also settles the server render, which is not a detail
 * on a prerendered page: `getServerSnapshot` returns the theme the bytes are
 * stamped with, so the markup React produces during the build matches the
 * markup it hydrates against. Returning the *browser's* theme there would be a
 * hydration mismatch on every visitor who prefers light.
 */
import { useSyncExternalStore } from 'react'

export type Theme = 'dark' | 'light'

/** Shared with `public/theme.js` and with the console. Written once here so a
 *  rename cannot leave the pre-paint script reading a key nothing writes. */
export const THEME_KEY = 'kimene.theme'

/** What the prerendered bytes carry, and what the server snapshot must be. */
export const PRERENDERED_THEME: Theme = 'dark'

const listeners = new Set<() => void>()

function read(): Theme {
  if (typeof document === 'undefined') return PRERENDERED_THEME
  return document.documentElement.getAttribute('data-theme') === 'light' ? 'light' : 'dark'
}

/**
 * The colours the browser paints its own chrome with, per theme.
 *
 * Duplicated from `--k-bg` because a `<meta>` content attribute cannot resolve a
 * custom property. `theme.test.ts` reads `tokens.css` and fails if either of
 * these stops matching the token, which is the only thing keeping two copies of
 * a colour honest.
 */
export const CHROME_COLOR: Record<Theme, string> = {
  dark: '#0b0e13',
  light: '#f7f8fa',
}

export function setTheme(next: Theme): void {
  document.documentElement.setAttribute('data-theme', next)
  try {
    localStorage.setItem(THEME_KEY, next)
  } catch {
    // A private window refuses to store. The choice still applies to this page;
    // it simply will not outlive it, which is better than refusing the click.
  }
  const meta = document.querySelector('meta[name="theme-color"]')
  if (meta) meta.setAttribute('content', CHROME_COLOR[next])
  listeners.forEach((l) => l())
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb)
  // Another tab on this origin — the site is five documents, so "another tab"
  // is also "the page the reader opened from this one".
  const onStorage = (e: StorageEvent) => {
    if (e.key !== THEME_KEY) return
    const value = e.newValue
    if (value === 'light' || value === 'dark') {
      document.documentElement.setAttribute('data-theme', value)
      const meta = document.querySelector('meta[name="theme-color"]')
      if (meta) meta.setAttribute('content', CHROME_COLOR[value])
      cb()
    }
  }
  window.addEventListener('storage', onStorage)
  return () => {
    listeners.delete(cb)
    window.removeEventListener('storage', onStorage)
  }
}

export function useTheme(): Theme {
  return useSyncExternalStore(subscribe, read, () => PRERENDERED_THEME)
}
